import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { ValidateOrgNumber, ValidationService } from '@vinnova/validation';
import { forkJoin, merge, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';
import { Country } from '../../../core/models/country.model';
import { CreateCompanyModel } from '../../../core/models/create-company.model';
import { ListCompany } from '../../../core/models/list-company.model';
import { SearchResult } from '../../../core/models/search-result.model';
import { CompaniesQuery } from '../../../core/queries/companies.query';
import { WorksitesQuery } from '../../../core/queries/worksites.query';

@UntilDestroy()
@Component({
  selector: 'lib-create-company',
  templateUrl: './create-company.component.html',
  styleUrls: ['./create-company.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateCompanyComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() form: FormGroup<any>;
  @Input() countries: Country[];
  @Input() disabled: boolean;
  @Input() isProjectPart: boolean;
  @Input() selectedCompany: ListCompany;

  duplicateCompanies$: Observable<ListCompany[]>;
  isDuplicated = false;

  @Output() createCompany = new EventEmitter<CreateCompanyModel>();
  @Output() selectCompany = new EventEmitter<ListCompany>();
  @Output() duplicated = new EventEmitter<boolean>();

  vatNotProvided: boolean;
  hideOverrideSelectButton = true;
  countryErrorMessage$: Observable<string>;
  nameErrorMessage$: Observable<string>;
  nationalRegistrationNumberErrorMessage$: Observable<string>;
  vatErrorMessage$: Observable<string>;
  phoneErrorMessage$: Observable<string>;
  commentErrorMessage$: Observable<string>;

  InputType = InputType;

  constructor(
    private validationService: ValidationService,
    private worksitesQuery: WorksitesQuery,
    private companiesQuery: CompaniesQuery,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit() {
    this.setupValueChangeListeners();
    this.setErrorMessage();
  }

  resetForm() {
    this.form.reset({ country: 'SE' });
  }

  onIntermediateChange(event) {
    if (event.target.checked) {
      this.nationalRegistrationNumber.setValidators([ValidateOrgNumber]);
      this.nationalRegistrationNumber.updateValueAndValidity();
    } else {
      this.nationalRegistrationNumber.setValidators([
        Validators.required,
        ValidateOrgNumber,
      ]);
      this.nationalRegistrationNumber.updateValueAndValidity();
    }
  }

  onSelectCompany(selectedCompany: ListCompany) {
    this.selectCompany.emit(selectedCompany);
  }

  get addressForm() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.form.get('address') as FormGroup<any>;
  }

  get country() {
    return this.form.get('address').get('country') as FormControl<string>;
  }

  get isForeign() {
    return this.form.get('isForeign') as FormControl<string>;
  }

  get nationalRegistrationNumber() {
    return this.form.get('nationalRegistrationNumber') as FormControl<string>;
  }

  get vat() {
    return this.form.get('vat') as FormControl<string>;
  }

  get vatMissing() {
    return this.form.get('vatMissing') as FormControl<string>;
  }

  get name() {
    return this.form.get('name') as FormControl<string>;
  }

  get comment() {
    return this.form.get('comment') as FormControl<string>;
  }

  get phone() {
    return this.form.get('phone') as FormControl<string>;
  }

  get municipality() {
    return this.addressForm.get('municipalityCode') as FormControl<string>;
  }

  get county() {
    return this.addressForm.get('countyCode') as FormControl<string>;
  }

  formatOrgNumber(event) {
    const orgNmbr = event.target.value.trim();
    if (this.isForeign.value || isNaN(orgNmbr) || orgNmbr.length !== 10) {
      return;
    }
    this.nationalRegistrationNumber.patchValue(
      orgNmbr.slice(0, 6) + '-' + orgNmbr.slice(6),
    );
  }

  private setupValueChangeListeners() {
    this.isForeign.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.vatMissing.reset();
        this.nationalRegistrationNumber.reset();
        this.vat.reset();
        this.municipality.reset();
        this.county.reset();
        if (value) {
          this.country.patchValue(undefined);
          this.vat.enable();
          this.nationalRegistrationNumber.disable();
          this.nationalRegistrationNumber.setValidators([Validators.required]);
          this.municipality.disable();
          this.county.disable();
        } else {
          this.country.patchValue('SE');
          this.nationalRegistrationNumber.enable();
          this.vat.disable();
          this.nationalRegistrationNumber.setValidators([
            Validators.required,
            ValidateOrgNumber,
          ]);
          this.municipality.enable();
          this.county.enable();
        }
        this.nationalRegistrationNumber.updateValueAndValidity();
      });

    this.vatMissing.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.nationalRegistrationNumber.reset();
        this.vat.reset();
        if (value) {
          this.nationalRegistrationNumber.enable();
          this.vat.disable();
        } else {
          this.nationalRegistrationNumber.disable();
          this.vat.enable();
        }
      });

    this.country.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        /*
         * När vi kommer till valueChanges så har inte värdet ändrats än på formcontrollen
         * Vi gör en patchValue på country-controllen här för att när
         * vi kollar om valideringen på zipcode behöver nya värdet finnas på country
         * Undviker oändlig loop genom distinctUntilChanged
         */
        this.country.patchValue(value);
        this.addressForm.get('zipCode').updateValueAndValidity();

        if (this.isForeign) {
          this.vat.reset();
          this.nationalRegistrationNumber.reset();
        }
      });

    this.duplicateCompanies$ = merge(
      this.nationalRegistrationNumber.valueChanges,
      this.vat.valueChanges,
    ).pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap((value) => {
        value = value?.replaceAll(/\s/g, '');
        return value && this.isValidCompanySearch()
          ? this.searchWorksitesAndCompaniesByCompanyNumber(value)
          : of(undefined);
      }),
      map((searchCompanyResult) => {
        this.hideOverrideSelectButton = true;
        const listCompanies = [];

        if (searchCompanyResult) {
          //Lägger till allt från worksite resultatet vilket är den primära sökningen.
          for (const listCompany of searchCompanyResult[0].result) {
            listCompanies.push(listCompany);
          }

          //Lägger till allt från company resultatet om organisationen inte redan finns i första sökresultatet.
          //Används för att hitta organisationer som saknar arbetsställen i Bisnode.
          for (const listCompany of searchCompanyResult[1].result) {
            const existingListCompany = listCompanies.find(
              (x) => x.providerId === listCompany.providerId,
            );

            if (existingListCompany) {
              existingListCompany.legalForm = listCompany.legalForm;
            } else {
              listCompanies.push(listCompany);
            }
          }
        }

        this.isDuplicated = listCompanies.length > 0 ? true : false;
        this.duplicated.emit(this.isDuplicated);
        return listCompanies;
      }),
    );
  }

  private isValidCompanySearch(): boolean {
    //Giltigt att söka på organisation om formuläret har land orgnr eller vat.
    return (
      this.country.value &&
      (this.nationalRegistrationNumber.valid || this.vat.valid)
    );
  }

  private searchWorksitesAndCompaniesByCompanyNumber(
    companyNumber: string,
  ): Observable<[SearchResult<ListCompany>, SearchResult<ListCompany>]> {
    return forkJoin([
      this.worksitesQuery.search(
        companyNumber,
        undefined,
        undefined,
        this.form.value.address.country,
      ),
      this.companiesQuery.search(
        companyNumber,
        this.form.value.address.country,
        true,
      ),
    ]);
  }

  setErrorMessage() {
    this.countryErrorMessage$ = this.validationService
      .isInvalid(this.country)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.country.errors.required) {
            return this.translocoService.translate('required');
          }
        }),
      );

    this.nameErrorMessage$ = this.validationService.isInvalid(this.name).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.name.errors.required) {
          return this.translocoService.translate('required');
        }

        if (this.name.errors.maxlength) {
          return this.translocoService.translate('maxlength');
        }
      }),
    );

    this.nationalRegistrationNumberErrorMessage$ = this.validationService
      .isInvalid(this.nationalRegistrationNumber)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.nationalRegistrationNumber.errors.validOrgNumber) {
            return this.translocoService.translate(
              'worksite_registration_number_invalid',
            );
          }

          if (this.nationalRegistrationNumber.errors.required) {
            return this.translocoService.translate('required');
          }

          if (this.nationalRegistrationNumber.errors.maxlength) {
            return this.translocoService.translate('maxlength');
          }
        }),
      );

    this.vatErrorMessage$ = this.validationService.isInvalid(this.vat).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.vat.errors.required) {
          return this.translocoService.translate('required');
        }

        if (this.vat.errors.maxlength) {
          return this.translocoService.translate('maxlength');
        }
      }),
    );

    this.phoneErrorMessage$ = this.validationService.isInvalid(this.phone).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.phone.errors.validPhoneNumber) {
          return this.translocoService.translate('phone_invalid');
        }
      }),
    );

    this.commentErrorMessage$ = this.validationService
      .isInvalid(this.comment)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.comment.errors.required) {
            return this.translocoService.translate('required');
          }

          if (this.comment.errors.maxlength) {
            return this.translocoService.translate('maxlength');
          }
        }),
      );
  }
}
